import React, { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { commerceRepository } from "../api";
import { Cart } from "../api/types";

const CART_ID_STORAGE_KEY = "petshop_cart_id";

interface CartContextType {
    cart: Cart | null;
    loading: boolean;
    isMutating: boolean;
    addItem: (variantId: string, quantity?: number) => Promise<void>;
    updateItem: (lineId: string, quantity: number) => Promise<void>;
    removeItem: (lineId: string) => Promise<void>;
    goToCheckout: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
    const [cart, setCart] = useState<Cart | null>(null);
    const [loading, setLoading] = useState(true);
    const [isMutating, setIsMutating] = useState(false);

    useEffect(() => {
        const existingCartId = localStorage.getItem(CART_ID_STORAGE_KEY);
        if (!existingCartId) {
            setLoading(false);
            return;
        }
        commerceRepository
            .getCart(existingCartId)
            .then((existingCart) => setCart(existingCart))
            .finally(() => setLoading(false));
    }, []);

    const ensureCart = useCallback(async (): Promise<Cart> => {
        if (cart) return cart;
        const existingCartId = localStorage.getItem(CART_ID_STORAGE_KEY);
        if (existingCartId) {
            const existingCart = await commerceRepository.getCart(existingCartId);
            if (existingCart) {
                setCart(existingCart);
                return existingCart;
            }
        }
        const newCart = await commerceRepository.createCart();
        localStorage.setItem(CART_ID_STORAGE_KEY, newCart.id);
        setCart(newCart);
        return newCart;
    }, [cart]);

    const addItem = useCallback(async (variantId: string, quantity: number = 1) => {
        setIsMutating(true);
        try {
            const currentCart = await ensureCart();
            const updatedCart = await commerceRepository.addCartLine(currentCart.id, variantId, quantity);
            setCart(updatedCart);
        } finally {
            setIsMutating(false);
        }
    }, [ensureCart]);

    const updateItem = useCallback(async (lineId: string, quantity: number) => {
        if (!cart) return;
        setIsMutating(true);
        try {
            const updatedCart = await commerceRepository.updateCartLine(cart.id, lineId, quantity);
            setCart(updatedCart);
        } finally {
            setIsMutating(false);
        }
    }, [cart]);

    const removeItem = useCallback(async (lineId: string) => {
        if (!cart) return;
        setIsMutating(true);
        try {
            const updatedCart = await commerceRepository.removeCartLine(cart.id, lineId);
            setCart(updatedCart);
        } finally {
            setIsMutating(false);
        }
    }, [cart]);

    const goToCheckout = useCallback(() => {
        if (!cart || cart.lines.length === 0) return;
        window.location.href = cart.checkoutUrl;
    }, [cart]);

    return (
        <CartContext.Provider value={{ cart, loading, isMutating, addItem, updateItem, removeItem, goToCheckout }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
}
